import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type {
  CasinoCategoryCounts,
  CategoryCountsByProduct,
} from "@/features/casino/models/casinoCategoryCounts";
import { useCasinoCategories } from "@/features/casino/hooks/useCasinoCategories";
import { useUserId } from "@/features/auth/hooks/useUserId";
import { useUserToken } from "@/features/auth/hooks/useUserToken";
import { apiQueryKey } from "@/hooks/api/queryKey";

const COUNTS_STALE_TIME = 30_000;
const EMPTY_COUNTS: CategoryCountsByProduct = {};

/**
 * Game counts per category and provider for this user's geo-IP country.
 * @param options.enabled false for a user without permission.
 * @param options.refetchInterval false loads once; defaults to polling.
 */
const useCasinoCategoryCounts = (
  categoryId?: string,
  providers?: string[],
  {
    enabled = true,
    refetchInterval = COUNTS_STALE_TIME,
  }: { enabled?: boolean; refetchInterval?: number | false } = {},
) => {
  // counts differ by user, so the key changes when the account does
  const userId = useUserId();
  const token = useUserToken();

  const { categories, isLoading: isLoadingCategories } =
    useCasinoCategories(enabled);

  const { data, error, isPending } = useQuery({
    queryKey: apiQueryKey("category-counts/by-product", { userId }),
    queryFn: async () => {
      const response = await fetch(
        `${process.env.GATSBY_API_URI}/category-counts/by-product`,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch category counts: ${response.status}`);
      }

      return (await response.json()) as CasinoCategoryCounts;
    },
    enabled,
    staleTime: COUNTS_STALE_TIME,
    refetchInterval,
    meta: { userScoped: true },
  });

  const countsByProduct = data?.categoryCountsByProduct ?? EMPTY_COUNTS;

  const { total, providersWithResults } = useMemo(() => {
    const supportedCategoryIds = new Set(
      categoryId ? [categoryId] : categories.map((category) => category.id),
    );

    return (
      providers?.length ? providers : Object.keys(countsByProduct)
    ).reduce(
      (acc, key) => {
        const categorySum = (countsByProduct[key] ?? []).reduce(
          (sum, entry) =>
            supportedCategoryIds.has(entry.categoryId)
              ? sum + entry.count
              : sum,
          0,
        );

        if (categorySum > 0) acc.providersWithResults.push(key);
        acc.total += categorySum;
        return acc;
      },
      { total: 0, providersWithResults: [] as string[] },
    );
  }, [countsByProduct, providers, categoryId, categories]);

  return {
    total,
    providersWithResults,
    categoryCountsByProduct: countsByProduct,
    country: data?.countryISOCode ?? "",
    error,
    isLoading: enabled && (isPending || isLoadingCategories),
  } as const;
};

export { useCasinoCategoryCounts };
